import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { MenuProduct } from "@/mocks/branch/menu";
import { useOrders } from "@/context/OrdersContext";
import { useMenu } from "@/context/MenuContext";
import { useSettings } from "@/context/SettingsContext";
import { useBranch } from "@/context/BranchContext";
import AccessDenied from "@/pages/branch/access-denied/page";
import type { OrderItem, PaymentMethod, PickupMethod } from "@/mocks/branch/orders";
import MenuBrowser from "./components/MenuBrowser";
import CartPanel from "./components/CartPanel";
import { vatBreakdown } from "@/pages/branch/orders/constants";
import { ORDER_TYPE_OPTIONS, type CartLine } from "./constants";

export default function PosPage() {
  const navigate = useNavigate();
  const { can } = useBranch();
  const { createPosOrder } = useOrders();
  const { categories, products } = useMenu();
  const { settings } = useSettings();

  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [lines, setLines] = useState<CartLine[]>([]);
  const [orderType, setOrderType] = useState<PickupMethod>("takeaway");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("pos_terminal");
  const [notes, setNotes] = useState("");
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [emptyWarning, setEmptyWarning] = useState(false);

  const branchClosed = settings.emergencyClosure.active;

  const availableMethods = useMemo(
    () => ORDER_TYPE_OPTIONS.filter((opt) => settings.pickupPolicy.methods[opt.value]),
    [settings.pickupPolicy.methods],
  );

  const itemCount = useMemo(() => lines.reduce((sum, l) => sum + l.quantity, 0), [lines]);

  useEffect(() => {
    if (!availableMethods.some((opt) => opt.value === orderType)) {
      const first = availableMethods[0];
      if (first) setOrderType(first.value);
    }
  }, [availableMethods, orderType]);

  const productById = useCallback(
    (id: string) => products.find((p) => p.id === id),
    [products],
  );

  const isAvailable = useCallback(
    (product: MenuProduct) => product.available !== false && product.quantity !== 0,
    [],
  );

  const addProduct = useCallback(
    (product: MenuProduct) => {
      if (!isAvailable(product)) return;
      setLines((prev) => {
        const existing = prev.find((l) => l.productId === product.id);
        if (existing) {
          const cap = product.quantity ?? Infinity;
          return prev.map((l) =>
            l.productId === product.id ? { ...l, quantity: Math.min(l.quantity + 1, cap) } : l,
          );
        }
        return [
          ...prev,
          { productId: product.id, name: product.name, unitPrice: product.price, quantity: 1 },
        ];
      });
    },
    [isAvailable],
  );

  const increment = useCallback(
    (productId: string) => {
      const product = productById(productId);
      const cap = product?.quantity ?? Infinity;
      setLines((prev) =>
        prev.map((l) =>
          l.productId === productId ? { ...l, quantity: Math.min(l.quantity + 1, cap) } : l,
        ),
      );
    },
    [productById],
  );

  const decrement = useCallback((productId: string) => {
    setLines((prev) =>
      prev
        .map((l) => (l.productId === productId ? { ...l, quantity: l.quantity - 1 } : l))
        .filter((l) => l.quantity > 0),
    );
  }, []);

  const remove = useCallback((productId: string) => {
    setLines((prev) => prev.filter((l) => l.productId !== productId));
  }, []);

  const clear = useCallback(() => setLines([]), []);

  const subtotal = useMemo(
    () => lines.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0),
    [lines],
  );

  const total = subtotal;
  const { vat } = vatBreakdown(total);

  const confirmOrder = useCallback(() => {
    if (branchClosed) return;
    if (lines.length === 0) {
      setEmptyWarning(true);
      window.setTimeout(() => setEmptyWarning(false), 2600);
      return;
    }
    const items: OrderItem[] = lines.map((l) => ({
      name: l.name,
      quantity: l.quantity,
      unitPrice: l.unitPrice,
    }));
    const order = createPosOrder({
      pickupMethod: orderType,
      paymentMethod,
      notes: notes.trim() || undefined,
      items,
      subtotal,
      total,
    });
    setLines([]);
    setNotes("");
    setMobileCartOpen(false);
    navigate(`/pos/success/${order.id}`);
  }, [lines, orderType, paymentMethod, notes, subtotal, total, createPosOrder, branchClosed, navigate]);

  const cartPanel = (
    <CartPanel
      lines={lines}
      orderType={orderType}
      paymentMethod={paymentMethod}
      notes={notes}
      subtotal={subtotal}
      vat={vat}
      total={total}
      availableMethods={availableMethods}
      branchClosed={branchClosed}
      estimatedMinutes={settings.pickupPolicy.estimatedMinutes}
      emptyWarning={emptyWarning}
      onIncrement={increment}
      onDecrement={decrement}
      onRemove={remove}
      onClear={clear}
      onNotesChange={setNotes}
      onOrderTypeChange={setOrderType}
      onPaymentChange={setPaymentMethod}
      onConfirm={confirmOrder}
    />
  );

  if (!can("pos")) {
    return <AccessDenied />;
  }

  return (
    <div className="h-[calc(100dvh-80px)] sm:h-[calc(100dvh-88px)] md:h-[calc(100dvh-112px)] flex flex-col bg-background-50 rounded-card border border-background-200 overflow-hidden">
      {branchClosed ? (
        <div className="flex items-center gap-3 bg-red-600 text-white px-4 md:px-5 py-3 shrink-0 animate-fade-slide-down">
          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shrink-0">
            <i className="ri-alarm-warning-line text-xl"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold">الفرع مغلق مؤقتًا — تعطيل إنشاء الطلبات</p>
            {settings.emergencyClosure.reason ? (
              <p className="text-xs text-white/80 mt-0.5 truncate">السبب: {settings.emergencyClosure.reason}</p>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="flex-1 flex min-h-0">
        <div className="w-full lg:w-[60%] border-l border-background-200 flex flex-col min-h-0">
          <MenuBrowser
            categories={categories}
            products={products}
            activeCategory={activeCategory}
            search={search}
            onCategoryChange={setActiveCategory}
            onSearchChange={setSearch}
            onAdd={addProduct}
          />
        </div>

        <div className="hidden lg:flex w-[40%] flex-col min-h-0">{cartPanel}</div>
      </div>

      <div className="lg:hidden shrink-0 border-t border-background-200 bg-background-50 px-4 py-3 flex items-center gap-3">
        <div className="min-w-0">
          <p className="text-xs text-foreground-500">المجموع الكلي</p>
          <p className="text-lg font-bold text-foreground-900 leading-tight">{total.toFixed(2)} ر.س</p>
        </div>
        <button
          type="button"
          onClick={() => setMobileCartOpen(true)}
          className="flex-1 h-12 rounded-btn bg-primary-500 text-white text-sm font-bold flex items-center justify-center gap-2 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-shopping-cart-2-line text-lg"></i>
          </div>
          عرض السلة
          {itemCount > 0 ? (
            <span className="min-w-6 h-6 px-1.5 rounded-full bg-white/20 text-white text-xs font-bold flex items-center justify-center">
              {itemCount}
            </span>
          ) : null}
        </button>
      </div>

      {mobileCartOpen ? (
        <div className="lg:hidden fixed inset-0 z-[60] flex flex-col justify-end">
          <div className="absolute inset-0 bg-foreground-900/50" onClick={() => setMobileCartOpen(false)}></div>
          <div className="relative flex flex-col h-[88dvh] bg-background-50 rounded-t-2xl overflow-hidden animate-slide-up">
            <button
              type="button"
              aria-label="إغلاق السلة"
              onClick={() => setMobileCartOpen(false)}
              className="absolute top-3 left-3 z-20 w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <div className="flex-1 min-h-0 pt-12">{cartPanel}</div>
          </div>
        </div>
      ) : null}

    </div>
  );
}